import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Input, InputNumber, DatePicker, Row, Col, Table, Typography, message, Modal, Space } from "antd";
import dayjs from "dayjs";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import { UiButton, ActionIconButton, UiTag } from "../../ui";
import {
  ListScreen,
  DetailHead,
  DetailGrid,
  DetailFull,
  DetailCard,
  DetailBody,
  FootTotal,
  BillTotals,
  BatchButton,
  AddLineButton,
  IconAction,
  RowActions,
  DetailEmpty,
  listTableClass,
} from "../common/ListScreen";
import CgIcon from "../common/CgIcon";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import GstSummaryTable from "../common/GstSummaryTable";
import InlinePurchaseHistory from "./InlinePurchaseHistory";
import { gstPercent, gstSplit, cessPercent } from "../common/gstMath";
import VendorFormModal from "../Configurations/VendorFormModal";
import BatchDetailsModal from "./BatchDetailsModal";
import MedicineFormModal from "../Medicines/MedicineFormModal";
import { getPharmacyConfigListApi } from "../../../services/pharmacyConfigs.services";
import { getPharmacyMedicineApi } from "../../../services/pharmacyMedicines.services";
import { fetchActivePharmacyMedicines } from "../common/medicineSearch";
import {
  getPharmacyPurchaseEntryApi,
  addPharmacyPurchaseEntryApi,
  updatePharmacyPurchaseEntryApi,
  addPharmacyPurchaseEntryItemApi,
  deletePharmacyPurchaseEntryItemApi,
  completePharmacyPurchaseEntryApi,
  cancelPharmacyPurchaseEntryApi,
  editPharmacyPurchaseEntryApi,
  addPharmacyPurchaseEntryEditItemApi,
  updatePharmacyPurchaseEntryEditItemApi,
  removePharmacyPurchaseEntryEditItemApi,
} from "../../../services/pharmacyPurchase.services";
import { formatAmount } from "../common/money";
import { rateAboveMrpError } from "../common/validators";
import fieldStyles from "../common/fieldWithAdd.module.css";
import listStyles from "../common/listScreen.module.css";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";
import AuditLogDrawer, { AUDIT_ENTITIES } from "../common/AuditLogDrawer";
import LeaveWithoutSavingModal from "../common/LeaveWithoutSavingModal";
import {
  canCancelDocument,
  canEditDocument,
  cancelFailureMessage,
  purchaseFailureMessage,
} from "../common/cancelWindow";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";

const footerActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 12,
};

const fetchVendors = (text) =>
  getPharmacyConfigListApi("vendors", new URLSearchParams({ search: text, limit: 20 })).then(
    (res) => (res?.data?.results || []).map((item) => ({ value: item.id, label: item.name }))
  );

const fetchVendorGstin = (vendorId) =>
  getPharmacyConfigListApi("vendors", new URLSearchParams({ limit: 200 })).then(
    (res) =>
      (res?.data?.results || []).find((v) => v.id === vendorId)?.gstin || ""
  );

const fetchPharmacyMedicines = fetchActivePharmacyMedicines;

/*
 * What a line comes to: the goods, less the discount off them, plus the GST
 * and the cess due on what is left.
 *
 * GST and cess are counted apart rather than rolled into one percentage. They
 * are two different levies and the screen prints them under two different
 * headings - rolling them together is what left the GST summary saying 4,975
 * while the bill beside it said 6,467.50 for the same line.
 */
const draftLineAmount = (draft) => {
  const taxable = Math.max((draft.qty || 0) * (draft.purchase_rate || 0) - (draft.discount || 0), 0);
  if (!draft.batch) return { taxable, gst: 0, cess: 0, amount: null };
  const gst = taxable * (gstPercent(draft.batch) / 100);
  const cess = taxable * (cessPercent(draft.batch) / 100);
  return { taxable, gst, cess, amount: taxable + gst + cess };
};


/*
 * The tax a scanned line carries, written the way the rest of the module
 * writes it: an equal pair, and the IGST they add up to.
 *
 * The scan reports either the two halves or one rate for the line. Either
 * way the pair has to be a pair - a scan that read 3 and 4 off a smudged
 * invoice is a scan to be corrected in the batch, not a rate to be charged.
 */
const scannedPair = (item, scannedGst) => {
  const sgst = Number(item?.sgst || 0);
  const cgst = Number(item?.cgst || 0);
  const half = sgst || cgst ? (sgst + cgst) / 2 : scannedGst / 2;
  const rounded = Number(half.toFixed(2));
  return { sgst: rounded, cgst: rounded, igst: Number((rounded * 2).toFixed(2)) };
};

const itemToLine = (item) => ({
  key: `item-${item.id}`,
  itemId: item.id,
  medicine: item.medicine
    ? { value: item.medicine, label: item.medicine_name || "" }
    : null,
  qty: Number(item.qty) || 0,
  free_qty: Number(item.free_qty) || 0,
  purchase_rate: Number(item.purchase_rate) || 0,
  discount: Number(item.discount) || 0,
  batch: {
    batch_number: item.batch_number || "",
    expiry: item.expiry || null,
    manufacturing_date: item.manufacturing_date || null,
    mrp: Number(item.mrp) || 0,
    selling_price: Number(item.selling_price) || 0,
    rate_b: Number(item.rate_b) || 0,
    rate_c: Number(item.rate_c) || 0,
    sgst: Number(item.sgst) || 0,
    cgst: Number(item.cgst) || 0,
    igst: Number(item.igst) || 0,
    cess: Number(item.cess) || 0,
  },
  medicineDefaults: { mrp: Number(item.mrp) || 0 },
  gstDefaults: {
    sgst: Number(item.sgst) || 0,
    cgst: Number(item.cgst) || 0,
    igst: Number(item.igst) || 0,
    cess: Number(item.cess) || 0,
  },
});

const linePayload = (line) => {
  const { taxable, amount } = draftLineAmount(line);
  const batch = line.batch || {};
  return {
    medicine: line.medicine?.value,
    batch_number: batch.batch_number,
    expiry: batch.expiry,
    manufacturing_date: batch.manufacturing_date,
    mrp: batch.mrp,
    selling_price: batch.selling_price,
    rate_b: batch.rate_b,
    rate_c: batch.rate_c,
    purchase_rate: line.purchase_rate,
    qty: line.qty,
    free_qty: line.free_qty,
    discount: line.discount,
    taxable_amount: taxable,
    sgst: batch.sgst,
    cgst: batch.cgst,
    igst: batch.igst,
    cess: batch.cess,
    amount,
  };
};


const readLines = (entry) =>
  (entry?.items || []).map((item) => {
    const line = itemToLine(item);
    return { ...line, saved: linePayload(line) };
  });

const lineIsDirty = (line) =>
  JSON.stringify(linePayload(line)) !== JSON.stringify(line.saved);

const PurchaseEntryPage = () => {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const isNew = !entryId || entryId === "new";
  const [form] = Form.useForm();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [newVendor, setNewVendor] = useState(null);
  const [addMedicineModalOpen, setAddMedicineModalOpen] = useState(false);
  /* The line that asked for a product to be created, so what comes back lands
     on it rather than arriving as another line. */
  const [addMedicineFor, setAddMedicineFor] = useState(null);
  /* The other answer to the same line: the medicine is in the catalogue
     already, under a name the parser did not recognise. */
  const [replaceFor, setReplaceFor] = useState(null);
  const [replaceChoice, setReplaceChoice] = useState(null);
  const [vendorGstin, setVendorGstin] = useState("");
  const selectedVendor = Form.useWatch("vendor", form);
  /* The rest of the header, watched for the same reason the vendor is: a
     change to any of them is a change to the entry. */
  const typedPartyNumber = Form.useWatch("party_number", form);
  const typedPurchaseDate = Form.useWatch("purchase_date", form);

  const [lines, setLines] = useState([]);
  const location = useLocation();
  const scannedInvoice = location.state?.scan;
  const [scanApplied, setScanApplied] = useState(false);
  const [batchModalKey, setBatchModalKey] = useState(null);
  const [additionalDiscount, setAdditionalDiscount] = useState(0);
  const [removedItemIds, setRemovedItemIds] = useState([]);
  const hydratedRef = useRef(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  /* What has happened to this entry, read beside it - Pharmacy Admins only,
     which is who the endpoint answers. */
  const [historyOpen, setHistoryOpen] = useState(false);

/*
 * An entry is finished with once the stock has moved or the entry has been
 * called off, and neither can be taken back - so both are read rather than
 * edited. The two are kept apart where it matters: only a completed entry has
 * a table of what actually went into stock.
 */
  const isPharmacyAdmin = useIsPharmacyAdmin();
  const isCompleted = entry?.status === "completed";
  const isCancelled = entry?.status === "cancelled";
  const isLocked = isCompleted || isCancelled;
  /*
   * Whether the entry may still be written to, on the same rule that governs
   * calling it off: a draft any time, a completed one inside the day it was
   * submitted, and a Pharmacy Admin whenever. An entry the server has never
   * seen is always being written.
   */
  const editable = !entry?.id || canEditDocument(entry, { isAdmin: isPharmacyAdmin });

  useEffect(() => {
    if (!isNew) {
    /* The document in hand belongs to the last id this screen was opened on.
       One route serves them all, so moving from one to another changes the
       parameter without remounting - and the previous one sits in state, whole
       and wrong, until the new one lands. A refresh of the same id is not that
       and must not blank the screen. */
      setEntry((prev) => (String(prev?.id) === String(entryId) ? prev : null));
      getPharmacyPurchaseEntryApi(entryId)
        .then((res) => {
          const data = res?.data;
          setEntry(data);
          setAdditionalDiscount(data?.discount || 0);
          form.setFieldsValue({
            vendor: data?.vendor,
            purchase_date: data?.purchase_date ? dayjs(data.purchase_date) : null,
            party_number: data?.party_number,
          });
          if (data?.id && hydratedRef.current !== data.id) {
            hydratedRef.current = data.id;
            setLines(readLines(data));
          }
        })
        .catch(() => message.error("Failed to load purchase entry"));
    }
  }, [entryId]);


  useEffect(() => {
    if (!scannedInvoice || scanApplied) return;
    setScanApplied(true);

    const invoice = scannedInvoice.invoice || {};
    form.setFieldsValue({
      purchase_date: invoice.invoice_date ? dayjs(invoice.invoice_date) : dayjs(),
      party_number: invoice.invoice_number || undefined,
    });
    applyScannedInvoice(scannedInvoice);
  }, [scannedInvoice, scanApplied]);

  const totals = useMemo(() => {
    const sums = lines.reduce(
      (acc, line) => {
        const figures = draftLineAmount(line);
        acc.taxable += figures.taxable;
        acc.gst += figures.gst;
        acc.cess += figures.cess;
        /* A line with no batch yet has no tax to it - it is worth the goods
           on it and nothing more. */
        acc.amount += figures.amount ?? figures.taxable;
        return acc;
      },
      { taxable: 0, gst: 0, cess: 0, amount: 0 }
    );
    const additionalDiscountAmount = sums.taxable * ((additionalDiscount || 0) / 100);
    return {
      ...sums,
      additionalDiscountAmount,
      grandTotal: sums.amount - additionalDiscountAmount,
    };
  }, [lines, additionalDiscount]);


  /*
   * The same lines, as the GST summary slabs them.
   *
   * The heads are filled from the spelling the rate was actually given in, so
   * SGST + CGST + IGST on a row adds up to the GST that row is charging -
   * they used to be filled from all three at once, which printed a 3% and a
   * 4% head under a 10% slab.
   */
  const gstLines = useMemo(
    () =>
      lines.map((line) => {
        const taxable = draftLineAmount(line).taxable;
        const batch = line.batch || {};
        const split = gstSplit(batch);
        const amountOf = (percent) => (taxable * percent) / 100;
        return {
          gst_percent: gstPercent(batch),
          taxable_amount: taxable,
          discount: Number(line.discount) || 0,
          sgst: amountOf(split.sgst),
          cgst: amountOf(split.cgst),
          igst: amountOf(split.igst),
          cess: amountOf(cessPercent(batch)),
        };
      }),
    [lines]
  );

  const showHeaderIsMissing = (errorFields) => {
    const field = errorFields?.[0]?.name;
    if (field) {
      form.scrollToField(field, { behavior: "smooth", block: "center" });
      form.getFieldInstance?.(field)?.focus?.();
    }
    message.error("Fill in the vendor details before adding products.");
  };


  const requireHeader = async () => {
    try {
      return await form.validateFields(["vendor", "purchase_date", "party_number"]);
    } catch (err) {
      showHeaderIsMissing(err?.errorFields);
      throw err;
    }
  };


  const saveHeader = async (values) => {
    const payload = {
      vendor: values.vendor,
      purchase_date: values.purchase_date.format("YYYY-MM-DD"),
      party_number: values.party_number,
      discount: additionalDiscount || 0,
    };
    const res = entry?.id
      ? await updatePharmacyPurchaseEntryApi(entry.id, payload)
      : await addPharmacyPurchaseEntryApi(payload);
    setEntry(res.data);
    hydratedRef.current = res.data.id;
    return res.data.id;
  };

  useEffect(() => {
    if (!selectedVendor) {
      setVendorGstin("");
      return undefined;
    }
    let cancelled = false;
    fetchVendorGstin(selectedVendor)
      .then((gstin) => {
        if (!cancelled) setVendorGstin(gstin);
      })
      .catch(() => {
        if (!cancelled) setVendorGstin("");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedVendor]);

  /*
   * One more line, empty.
   *
   * The medicine used to be picked from a box above the table and the row
   * appeared already filled in. The design adds the row first and chooses in
   * it, which is also how the batch is picked - so the table is the one place
   * a line is built.
   */
  const handleAddBlankLine = async () => {
    try {
      await requireHeader();
    } catch {
      /* An incomplete header has already been pointed at. */
      return;
    }

    const key = `draft-new-${Date.now()}-${lines.length}`;
    setRowBackup(null);
    setEditingKey(entry?.id ? key : null);
    setLines((prev) => [
      ...prev,
      {
        key,
        medicine: null,
        qty: 1,
        free_qty: 0,
        purchase_rate: 0,
        discount: 0,
        batch: null,
        medicineDefaults: {},
        gstDefaults: { sgst: 0, cgst: 0, igst: 0, cess: 0 },
      },
    ]);
  };

  /*
   * A product created here is created to be bought on this entry, so the same
   * rule applies as to a line: the invoice needs a vendor first.
   */
  const handleAddProduct = async () => {
    try {
      await requireHeader();
    } catch {
      return;
    }
    setAddMedicineModalOpen(true);
  };

  const handleSelectProduct = async (value, option) => {
    try {
      await requireHeader();
    } catch {
      return;
    }

    const key = `draft-${value}-${lines.length}-${option.label}`;
    const draft = {
      key,
      medicine: { value, label: option.label },
      qty: 1,
      free_qty: 0,
      purchase_rate: 0,
      discount: 0,
      batch: null,
      medicineDefaults: {},
      gstDefaults: { sgst: 0, cgst: 0, igst: 0, cess: 0 },
    };
    setLines((prev) => [...prev, draft]);

    getPharmacyMedicineApi(value)
      .then((res) => {
        const medicine = res?.data;
        setLines((prev) =>
          prev.map((row) =>
            row.key === key
              ? {
                  ...row,
                  purchase_rate: medicine?.purchase_rate || 0,
                  medicineDefaults: {
                    mrp: medicine?.mrp || 0,
                    selling_price: medicine?.rate_a || 0,
                    rate_b: medicine?.rate_b || 0,
                    rate_c: medicine?.rate_c || 0,
                  },
                  gstDefaults: {
                    sgst: medicine?.sgst || 0,
                    cgst: medicine?.cgst || 0,
                    igst: medicine?.igst || 0,
                    cess: medicine?.cess || 0,
                  },
                }
              : row
          )
        );
      })
      .catch(() => {});
  };

  const suggestionToMedicine = (suggestion) => {
    if (!suggestion) return null;
    if (typeof suggestion === "number") return { value: suggestion, label: "" };

    const idKeys = [
      "id",
      "value",
      "medicine_id",
      "medicine",
      "pharmacy_medicine",
      "pharmacy_medicine_id",
      "record_id",
    ];
    const key = idKeys.find((k) => Number.isFinite(Number(suggestion[k])) && suggestion[k] !== null);
    const value = key ? Number(suggestion[key]) : null;
    if (!value) return null;

    const label =
      suggestion.name ??
      suggestion.label ??
      suggestion.medicine_name ??
      suggestion.pharmacy_medicine_name ??
      "";
    return { value, label };
  };

  const applyScannedInvoice = (scan) => {
    const scannedItems = scan?.items || [];
    if (!scannedItems.length) return;

    const drafts = scannedItems.map((item, index) => {
      const suggestion = item?.medicine_suggestions?.[0];
      /* Named apart from the `gstPercent` rule this file imports - the scan
         reports one figure for the line, which the pair is split out of. */
      const scannedGst = Number(item?.gst_percent || 0);
      return {
        key: `scan-${index}-${item?.batch_number || item?.s_no || index}`,

        medicine: suggestionToMedicine(suggestion),
        scannedName: item?.product_name || "",
        needsReview: Boolean(item?.needs_review),
        qty: Number(item?.qty || 0),
        free_qty: Number(item?.free_qty || 0),
        purchase_rate: Number(item?.purchase_rate || 0),
        discount: Number(item?.discount || 0),
        batch: {
          batch_number: item?.batch_number || "",
          expiry: item?.expiry || null,
          manufacturing_date: null,
          mrp: Number(item?.mrp || 0),
          selling_price: Number(item?.mrp || 0),
          rate_b: 0,
          rate_c: 0,
          /* A scanned invoice gives the tax as the intra-state pair, so IGST
             is the two added up rather than a third figure left at nil. */
          ...scannedPair(item, scannedGst),
          cess: 0,
        },
        medicineDefaults: {
          mrp: Number(item?.mrp || 0),
          selling_price: Number(item?.mrp || 0),
          rate_b: 0,
          rate_c: 0,
        },
        gstDefaults: {
          sgst: scannedGst / 2,
          cgst: scannedGst / 2,
          igst: scannedGst,
          cess: 0,
        },
      };
    });

    setLines(drafts);

    const unmatched = drafts.filter((d) => !d.medicine).length;
    message.info(
      unmatched
        ? `${drafts.length} line(s) loaded. ${unmatched} need a medicine picked before the entry can be completed.`
        : `${drafts.length} line(s) loaded from the invoice. Review them, then complete the entry.`
    );
  };

  /*
   * What a line may not be bought above. The batch carries the MRP once one has
   * been entered; before that the medicine's own is the best figure there is.
   */
  const draftMrp = (row) => row?.batch?.mrp ?? row?.medicineDefaults?.mrp ?? null;

  const draftRateError = (row) =>
    rateAboveMrpError(row?.purchase_rate, draftMrp(row));

  const updateDraft = (key, patch) => {
    setLines((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  /*
   * Taking a line off the table. One already on the server is remembered so it
   * can be deleted when the entry is completed - the table is the working copy
   * until then, and nothing is undone on the server behind it.
   */
  /*
   * Editing a line of an entry the server already holds: the pencil opens one
   * line, the tick keeps what was typed and the cross puts the line back. All
   * of it stays in the browser - the footer is what writes.
   */
  const [editingKey, setEditingKey] = useState(null);
  const [rowBackup, setRowBackup] = useState(null);
  const isEditingRow = (row) => !entry?.id || editingKey === row.key;

  /*
   * The line whose purchase history is open underneath it.
   *
   * Selecting a line opens its ledger, as the sale entry grid does - a click
   * that lands on one of the row's own controls is the pharmacist typing,
   * not asking, so it is left alone. A line with no medicine on it yet has
   * nothing to look up.
   */
  const [historyKey, setHistoryKey] = useState(null);

  const handleRowClick = (event, row) => {
    const target = event?.target;
    if (
      target?.closest?.(
        "input, button, a, label, textarea, .ant-select, .ant-input-number, .ant-picker, [role='button']"
      )
    ) {
      return;
    }
    if (!row?.medicine?.value) return;
    setHistoryKey((open) => (open === row.key ? null : row.key));
  };

  /* A line that loses its medicine - or is taken off - takes its ledger with
     it rather than leaving one open under the row that replaced it. */
  useEffect(() => {
    if (!historyKey) return;
    const row = lines.find((line) => line.key === historyKey);
    if (!row || !row.medicine?.value) setHistoryKey(null);
  }, [lines, historyKey]);

  const startRowEdit = (row) => {
    setRowBackup({ key: row.key, row });
    setEditingKey(row.key);
  };

  const commitRowEdit = () => {
    setRowBackup(null);
    setEditingKey(null);
  };

  const revertRowEdit = () => {
    if (rowBackup) {
      setLines((prev) =>
        prev.map((r) => (r.key === rowBackup.key ? rowBackup.row : r))
      );
    } else if (editingKey) {
      setLines((prev) => prev.filter((r) => r.key !== editingKey));
    }
    setRowBackup(null);
    setEditingKey(null);
  };

  const removeLine = (key) => {
    const line = lines.find((row) => row.key === key);
    if (line?.itemId) setRemovedItemIds((prev) => [...prev, line.itemId]);
    setLines((prev) => prev.filter((row) => row.key !== key));
  };

  /* What is missing before a line can be bought, named for the row it is on. */
  const lineProblem = (line) => {
    const name = line.medicine?.label || line.scannedName || "A line";
    if (!line.medicine?.value) return "Pick a medicine on every line.";
    if (!line.batch) return `${name}: add the batch details.`;
    if (!line.qty) return `${name}: enter a quantity.`;
    const rateError = draftRateError(line);
    return rateError ? `${name}: ${rateError}` : null;
  };

  /*
   * The table, written to the entry: lines taken off are deleted, new ones are
   * added, and ones read in from the server are rewritten only where they have
   * actually been edited. The server has no way to patch a line, so an edited
   * one goes as a delete and an add.
   */
  const writeLines = async (id) => {
    for (const itemId of removedItemIds) {
      await deletePharmacyPurchaseEntryItemApi(itemId);
    }
    setRemovedItemIds([]);

    for (const line of lines) {
      if (line.itemId && !lineIsDirty(line)) continue;
      if (line.itemId) await deletePharmacyPurchaseEntryItemApi(line.itemId);
      await addPharmacyPurchaseEntryItemApi(id, linePayload(line));
    }
  };

  /*
   * Everything the screen is holding, written to the server: the header first
   * because the lines hang off it, then the lines themselves. The entry stays
   * a draft - completing it is a separate decision taken below.
   *
   * Returns the entry's id, or null when it could not be written, in which
   * case the reason is already on screen.
   */
  const writeEverything = async () => {
    let values;
    try {
      values = await requireHeader();
    } catch {
      return null;
    }

    if (!lines.length) {
      message.warning("Add at least one product first");
      return null;
    }
    const problem = lines.map(lineProblem).find(Boolean);
    if (problem) {
      message.error(problem);
      return null;
    }

    if (entry?.id) {
      /*
       * A line at a time, against the entry's own edit endpoints.
       *
       * The header goes up on its own, then only the lines that actually
       * moved: one PATCH for a line that changed, one POST for a line added,
       * one DELETE for a line taken off. Nothing is said about a line nobody
       * touched.
       *
       * This replaces the whole-list PUT, which was a replace - it rewrote
       * every line on the entry whether or not it had been edited, and logged
       * every one of them as changed. The PUT is still the right call for a
       * save that genuinely means "these are now the lines", which this is
       * not: this is a correction to one of them.
       *
       * On a completed entry the server moves stock by the difference and
       * refuses where that stock has already gone. Changing the medicine on a
       * line replaces it, so the item id changes - which is why the entry is
       * read back afterwards rather than patched in the browser.
       */
      const header = {
        vendor: values.vendor,
        purchase_date: values.purchase_date.format("YYYY-MM-DD"),
        party_number: values.party_number,
        discount: additionalDiscount || 0,
      };

      /*
       * The header of a completed entry can only go through `edit/`.
       *
       * The plain PATCH on the entry is refused once it is completed - "Only
       * draft purchase entries can be edited" - and `edit/` is a replace, so
       * it has to carry the whole line list with it. That is the one case
       * that still writes every line, and it is the honest one: the caller
       * really is saying "these are now the lines".
       */
      if (headerDirty && entry.status !== "draft") {
        await editPharmacyPurchaseEntryApi(entry.id, {
          ...header,
          items: lines.map(linePayload),
        });
        setRemovedItemIds([]);
      } else {
        if (headerDirty) await saveHeader(values);

        for (const itemId of removedItemIds) {
          await removePharmacyPurchaseEntryEditItemApi(entry.id, itemId);
        }
        setRemovedItemIds([]);

        for (const line of lines) {
          if (!line.itemId) {
            await addPharmacyPurchaseEntryEditItemApi(entry.id, linePayload(line));
          } else if (lineIsDirty(line)) {
            await updatePharmacyPurchaseEntryEditItemApi(
              entry.id,
              line.itemId,
              linePayload(line)
            );
          }
        }
      }

      const fresh = await getPharmacyPurchaseEntryApi(entry.id);
      if (fresh?.data) {
        setEntry(fresh.data);
        setLines(readLines(fresh.data));
      }
      return entry.id;
    }

    const id = await saveHeader(values);
    await writeLines(id);
    return id;
  };

  /*
   * Writing a completed entry over, inside the window that allows it.
   *
   * The entry is already completed and the stock has already moved - this
   * only rewrites what it says it moved. Offered on a locked entry solely
   * once something has actually been changed, so a completed entry opened to
   * be read still shows nothing but Cancel.
   */
  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const id = await writeEverything();
      if (id) message.success("Purchase entry saved");
    } catch (err) {
      message.error(
        purchaseFailureMessage(err, "Failed to save the purchase entry")
      );
      /*
       * Put the header back to what the server still holds.
       *
       * A refused save leaves the typed value sitting in the box, so a vendor
       * the server would not accept went on reading as this entry's vendor -
       * the error said no and the screen said yes. The form is set back to
       * the entry as it stands, which is what was actually saved.
       */
      form.setFieldsValue({
        vendor: entry?.vendor,
        purchase_date: entry?.purchase_date ? dayjs(entry.purchase_date) : null,
        party_number: entry?.party_number,
      });
    } finally {
      setLoading(false);
    }
  };

  /*
   * The one call the screen makes on its own account.
   *
   * Everything above is held in the browser - the header, the lines, what was
   * taken off - and goes up in order here, and then the entry is completed and
   * the stock moves.
   */
  const handleComplete = async () => {
    setLoading(true);
    try {
      const id = await writeEverything();
      if (!id) return;
      const res = await completePharmacyPurchaseEntryApi(id);
      setEntry(res.data);
      /* The completed entry comes back with the lines as they were written.
         A response that carries none is not an entry without lines - it is a
         response that does not repeat them, so the table is left alone. */
      if (res?.data?.items?.length) setLines(readLines(res.data));
      message.success("Purchase entry completed, stock updated");
      if (isNew) navigate(`/pharmacy/purchase-entry/${res.data.id}`, { replace: true });
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to complete purchase entry"
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Calling the entry off.
   *
   * A draft can go at any time. A completed entry has already put the goods
   * into stock, so it can only be called off inside the day it was submitted -
   * and a Pharmacy Admin is not held to that. It is asked about either way: a
   * cancelled entry cannot be reopened.
   */
  const canCancel = canCancelDocument(entry, { isAdmin: isPharmacyAdmin });

  const handleCancelEntry = () => {
    Modal.confirm({
      title: "Cancel this purchase entry?",
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () =>
        cancelPharmacyPurchaseEntryApi(entry.id)
          .then(() => {
            message.success("Purchase entry cancelled");
            navigate("/pharmacy/purchase-entry");
          })
          .catch((err) =>
            message.error(
              cancelFailureMessage(err, "Failed to cancel the purchase entry")
            )
          ),
    });
  };

  /*
   * Whether the bar at the foot has anything in it.
   *
   * It used to render before the entry had arrived, so opening a cancelled
   * entry - or a completed one past its window - flashed up Cancel and
   * Complete Purchase Entry for a second and then took them away again.
   * Nothing is offered until the entry is known, and an entry with nothing
   * left to do with it gets no bar at all.
   */
  const entryKnown = isNew || String(entry?.id) === String(entryId);
  const showFooter = entryKnown && (editable || canCancel);

  /*
   * Leaving.
   *
   * Nothing on this screen reaches the server until the entry is completed, so
   * what is worth stopping to ask about is exactly what has not been sent: a
   * line added or edited, a line taken off, or a header filled in on an entry
   * that does not exist yet. Opening a completed entry, reading it and going
   * back changes nothing and is let through without a word.
   */
  /*
   * Whether the header has been changed on an entry the server already holds.
   *
   * Only the lines used to count. Changing the vendor, the date or the party
   * number on a saved entry left the screen believing nothing had happened -
   * so no Save changes appeared in the footer, and the change could only be
   * written by also touching a line. The header is part of the entry; moving
   * it is editing it.
   */
  const headerDirty =
    Boolean(entry?.id) &&
    (String(selectedVendor ?? "") !== String(entry?.vendor ?? "") ||
      String(typedPartyNumber ?? "") !== String(entry?.party_number ?? "") ||
      (typedPurchaseDate
        ? typedPurchaseDate.format("YYYY-MM-DD")
        : null) !== (entry?.purchase_date || null) ||
      Number(additionalDiscount || 0) !== Number(entry?.discount || 0));

  const hasUnsavedWork =
    editable &&
    (lines.some((line) => !line.itemId || lineIsDirty(line)) ||
      removedItemIds.length > 0 ||
      headerDirty ||
      (!entry?.id && Boolean(selectedVendor)));

  const leaveScreen = () => {
    if (!hasUnsavedWork) {
      navigate("/pharmacy/purchase-entry");
      return;
    }
    setLeaveOpen(true);
  };

  /* Save and go: the entry is written exactly as completing would write it and
     stopped there, so it sits as a draft with the work on it. */
  const saveAndLeave = async () => {
    setLoading(true);
    try {
      const id = await writeEverything();
      if (!id) return;
      message.success("Purchase entry saved as a draft.");
      setLeaveOpen(false);
      navigate("/pharmacy/purchase-entry");
    } catch (err) {
      message.error(
        purchaseFailureMessage(err, "Failed to save the purchase entry")
      );
    } finally {
      setLoading(false);
    }
  };

  /* Saving the header on its own, for an entry being put together over more
     than one sitting. The lines stay where they are. */
  const handleSaveVendorDetails = async () => {
    let values;
    try {
      values = await requireHeader();
    } catch {
      return;
    }
    setLoading(true);
    try {
      const id = await saveHeader(values);
      message.success("Vendor details saved");
      if (isNew) navigate(`/pharmacy/purchase-entry/${id}`, { replace: true });
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save vendor details"
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * One set of columns, read or typed a line at a time.
   *
   * An entry being built from nothing is every line at once - there is no
   * saved copy to protect. An entry the server already holds is corrected a
   * line at a time: the pencil opens one, the tick keeps what was typed, the
   * cross puts the line back as it was. None of the three touches the server;
   * the footer is what writes.
   */
  const columns = [
    {
      title: "Medicine",
      key: "medicine",
      /* The name and its flag read across the top of the cell; the way to
         put it right sits under them rather than pushing every figure on the
         row off the end of the table. */
      width: 230,
      render: (_, row) => {
        if (row.medicine && !isEditingRow(row)) {
          return <span className={listStyles.medName}>{row.medicine.label}</span>;
        }

        if (row.medicine) {
          return (
            <AsyncSearchSelect
              fetcher={fetchPharmacyMedicines}
              placeholder="Select medicine"
              value={row.medicine.value}
              initialOption={{ value: row.medicine.value, label: row.medicine.label }}
              onChange={(value, option) =>
                updateDraft(row.key, {
                  medicine: value ? { value, label: option?.label } : null,
                })
              }
            />
          );
        }

        /*
         * A line off a scanned invoice that the catalogue has nothing for.
         *
         * It reads as what the invoice called it, said plainly, with what is
         * wrong with it beside the name and the way to put it right next to
         * that - the product is created from here and lands back on this line.
         * The search stays underneath for a name the parser simply missed.
         */
        if (row.scannedName) {
          return (
            <div className={listStyles.unmatched}>
              <div className={listStyles.unmatchedHead}>
                <span className={listStyles.unmatchedName} title={row.scannedName}>
                  {row.scannedName}
                </span>
                <span className={listStyles.unmatchedFlag}>
                  <CgIcon name="info" size={13} stroke={2.1} />
                  Not in catalogue
                </span>
              </div>
              <div className={listStyles.unmatchedFix}>
                <UiButton
                  size="sm"
                  onClick={() => {
                    setAddMedicineFor(row.key);
                    setAddMedicineModalOpen(true);
                  }}
                >
                  <PlusOutlined /> Add Medicine
                </UiButton>
                {/* The invoice's name may be one the catalogue already holds
                    under a different spelling - this picks it by hand. */}
                <UiButton
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setReplaceChoice(null);
                    setReplaceFor(row.key);
                  }}
                >
                  <CgIcon name="swap" size={14} stroke={2.1} /> Replace Medicine
                </UiButton>
              </div>
            </div>
          );
        }

        /* A line just added, with nothing on it yet. */
        return (
          <AsyncSearchSelect
            fetcher={fetchPharmacyMedicines}
            placeholder="Select medicine"
            value={undefined}
            onChange={(value, option) =>
              updateDraft(row.key, {
                medicine: value ? { value, label: option?.label } : null,
              })
            }
          />
        );
      },
    },
    {
      title: "Batch",
      key: "batch",
      // A long unbroken batch number has no wrap point, so it stretched the
      // table far wider than the viewport and the page could not be scrolled
      // back. Capped and truncated, with the full value on hover.
      width: 128,
      render: (_, row) => (
        <BatchButton
          batch={row.batch?.batch_number}
          expiry={row.batch?.expiry ? dayjs(row.batch.expiry).format("DD MMM YYYY") : null}
          disabled={!isEditingRow(row)}
          onClick={() => setBatchModalKey(row.key)}
        />
      ),
    },
    {
      title: "Qty",
      key: "qty",
      width: 72,
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            style={{ width: 62 }}
            value={row.qty}
            onChange={(value) => updateDraft(row.key, { qty: value })}
          />
        ) : (
          row.qty
        ),
    },
    {
      title: "Free",
      key: "free_qty",
      width: 72,
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            style={{ width: 62 }}
            value={row.free_qty}
            onChange={(value) => updateDraft(row.key, { free_qty: value })}
          />
        ) : (
          row.free_qty
        ),
    },
    {
      title: "P.R",
      key: "purchase_rate",
      /* The rate carries its own complaint under it, which is what used to
         stretch this column across the screen. Fixed, the sentence wraps. */
      width: 104,
      render: (_, row) => {
        if (!isEditingRow(row)) return formatAmount(row.purchase_rate);
        const error = draftRateError(row);
        return (
          <>
            <InputNumber
              min={0}
              style={{ width: 94 }}
              status={error ? "error" : undefined}
              value={row.purchase_rate}
              onChange={(value) => updateDraft(row.key, { purchase_rate: value })}
            />
            {error ? (
              <Typography.Text
                type="danger"
                style={{ display: "block", fontSize: 11, lineHeight: 1.35, marginTop: 4 }}
              >
                {error}
              </Typography.Text>
            ) : null}
          </>
        );
      },
    },
    {
      title: "Discount",
      key: "discount",
      width: 88,
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            style={{ width: 78 }}
            value={row.discount}
            onChange={(value) => updateDraft(row.key, { discount: value })}
          />
        ) : (
          formatAmount(row.discount)
        ),
    },
    {
      title: "Amount",
      key: "taxable_amount",
      align: "right",
      width: 92,
      render: (_, row) => formatAmount(draftLineAmount(row).taxable),
    },
    {
      title: "Total",
      key: "amount",
      align: "right",
      width: 92,
      render: (_, row) => formatAmount(draftLineAmount(row).amount),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      width: 96,
      render: (_, row) => {
        if (!editable) return null;

        /* An entry with nothing saved yet is typed straight into, so its
           lines only need taking off. */
        if (!entry?.id) {
          return (
            <RowActions>
              <IconAction
                icon="trash"
                label="Remove line"
                tone="danger"
                onClick={() => removeLine(row.key)}
              />
            </RowActions>
          );
        }

        return (
          <RowActions>
            {editingKey === row.key ? (
              <>
                <IconAction
                  icon="check"
                  label="Keep these changes"
                  onClick={commitRowEdit}
                />
                <IconAction
                  icon="close"
                  label="Undo this line"
                  tone="danger"
                  onClick={revertRowEdit}
                />
              </>
            ) : (
              <>
                <IconAction
                  icon="edit"
                  label={`Edit ${row.medicine?.label || "line"}`}
                  disabled={editingKey !== null}
                  onClick={() => startRowEdit(row)}
                />
                <IconAction
                  icon="trash"
                  label="Remove line"
                  tone="danger"
                  disabled={editingKey !== null}
                  onClick={() => removeLine(row.key)}
                />
              </>
            )}
          </RowActions>
        );
      },
    },
  ];

  /*
   * A completed entry is a record rather than a form, so its lines are printed
   * instead of offered for editing - the same columns, read rather than typed.
   */
  const completedColumns = [
    {
      title: "Medicine",
      key: "medicine",
      render: (_, row) =>
        row.medicine?.label ? (
          <span className={listStyles.medName}>{row.medicine.label}</span>
        ) : (
          "-"
        ),
    },
    {
      title: "Batch",
      key: "batch",
      width: 128,
      render: (_, row) => (
        <BatchButton
          disabled
          batch={row.batch?.batch_number}
          expiry={row.batch?.expiry ? dayjs(row.batch.expiry).format("DD MMM YYYY") : null}
        />
      ),
    },
    { title: "Qty", dataIndex: "qty", key: "qty" },
    { title: "Free", dataIndex: "free_qty", key: "free_qty" },
    {
      title: "P.R",
      key: "purchase_rate",
      align: "right",
      render: (_, row) => formatAmount(row.purchase_rate),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, row) => formatAmount(row.discount),
    },
    {
      title: "Amount",
      key: "taxable_amount",
      render: (_, row) => formatAmount(draftLineAmount(row).taxable),
    },
    {
      title: "Total",
      key: "amount",
      render: (_, row) => formatAmount(draftLineAmount(row).amount),
    },
  ];

  const lineColumns = editable ? columns : completedColumns;

  const draftForBatchModal = lines.find((row) => row.key === batchModalKey);



  return (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout compact-footer-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
      footer={
        /*
         * A completed entry keeps a footer only while it can still be called
         * off - the day after it was submitted there is nothing left to do
         * with it, and a bar with one dead button in it is worse than no bar.
         */
        !showFooter ? undefined : (
          <div style={footerActionsStyle}>
            {/*
              * Cancel calls the entry off where there is an entry to call off,
              * and is the way out before that - a new entry that was never
              * saved has nothing on the server to cancel.
              */}
            <UiButton
              variant="danger"
              onClick={canCancel ? handleCancelEntry : leaveScreen}
            >
              Cancel
            </UiButton>
            {/*
              * An entry still being put together is completed from here. One
              * already completed offers nothing but the Cancel above until a
              * line is actually changed - and then the way to write that
              * change back, for as long as the window allows it.
              */}
            {isLocked ? (
              editable && hasUnsavedWork ? (
                <UiButton
                  loading={loading}
                  disabled={!lines.length}
                  onClick={handleSaveChanges}
                >
                  Save changes
                </UiButton>
              ) : null
            ) : (
              <UiButton
                loading={loading}
                disabled={!lines.length}
                onClick={handleComplete}
              >
                Complete Purchase Entry
              </UiButton>
            )}
          </div>
        )
      }
    >
      <div
        style={{
          padding: 24,
          paddingTop: 0,
          paddingBottom: showFooter ? 120 : 24,
        }}
      >
        <ListScreen>
          <DetailHead
            backLabel="Back to Purchase Entry"
            onBack={leaveScreen}
            title={entry?.entry_number || "Add Purchase Entry"}
            meta={
              entry?.status ? (
                <UiTag tone={entry.status === "cancelled" ? "warning" : "success"} size="sm">
                  {entry.status.replaceAll("_", " ")}
                </UiTag>
              ) : null
            }
            actions={
              entry?.id && isPharmacyAdmin ? (
                <UiButton variant="secondary" onClick={() => setHistoryOpen(true)}>
                  <CgIcon name="ledger" size={16} stroke={2} />
                  Audit log
                </UiButton>
              ) : null
            }
          />

        <DetailGrid>
        <DetailFull>
        <DetailCard title="Invoice Header">
          <DetailBody>
          <Form layout="vertical" form={form} disabled={!editable}>
            {/*
              * The four the design heads an invoice with. Three are the
              * entry's own - the vendor it came from, its party number and
              * the date it was purchased on; the fourth belongs to the
              * vendor, so it is shown rather than asked for.
              */}
            <Row gutter={[14, 8]}>
              <Col xs={24} md={12} lg={6}>
                <Form.Item label="Vendor" name="vendor" rules={[{ required: true, message: "Please select vendor" }]}>
                  <div className={fieldStyles.fieldWithAdd}>
                    <AsyncSearchSelect
                      fetcher={fetchVendors}
                      loadOnOpen
                      placeholder="Search vendor"
                      initialOption={
                        newVendor ||
                        (entry?.vendor
                          ? { value: entry.vendor, label: entry.vendor_name }
                          : undefined)
                      }
                      onChange={(value) => form.setFieldValue("vendor", value)}
                      value={selectedVendor}
                      disabled={!editable}
                    />
                    <UiButton disabled={!editable} onClick={() => setVendorModalOpen(true)}>
                      +
                    </UiButton>
                  </div>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item
                  label="Party Number"
                  name="party_number"
                >
                  <Input placeholder="Party Number" disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item
                  label="Purchase Date"
                  name="purchase_date"
                  /* Stock is nearly always entered on the day it arrives, so
                     the entry opens on today and is changed on the days it
                     is not - the same default a purchase order and a return
                     open on. An entry loaded from the server overwrites it
                     with the date it was actually bought on. */
                  initialValue={dayjs()}
                  rules={[{ required: true, message: "Please select the purchase date" }]}
                >
                  <DatePicker style={{ width: "100%" }} disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item label="GSTIN">
                  <Input value={vendorGstin} readOnly placeholder="—" />
                </Form.Item>
              </Col>
            </Row>
            {/*
              * Only while there is no entry yet.
              *
              * The button creates the entry from its header so the lines have
              * something to hang off. Once the entry exists the header is
              * written with everything else, and pressing this again posts a
              * create for a record that is already there - which the server
              * refuses, and rightly.
              */}
            {editable && !entry?.id && (
              <UiButton variant="secondary" loading={loading} onClick={handleSaveVendorDetails}>
                Save Vendor Details
              </UiButton>
            )}
          </Form>
          </DetailBody>
        </DetailCard>
        </DetailFull>

        <DetailFull>
        <DetailCard
          title="Items"
          count={lines.length}
          /* Nothing to total and nothing to add to until the first product
             is picked - the empty state above offers that. */
          foot={
            lines.length > 0 ? (
              <FootTotal label="Invoice total" value={formatAmount(totals.grandTotal)}>
                {editable ? (
                  <AddLineButton onClick={handleAddBlankLine}>
                    Add Item
                  </AddLineButton>
                ) : null}
              </FootTotal>
            ) : null
          }
          extra={
            editable && (
              <UiButton onClick={handleAddProduct}>
                <PlusOutlined /> Add Product
              </UiButton>
            )
          }
        >
          {lines.length === 0 ? (
            <DetailEmpty
              title="No products added"
              hint={
                'Click "Add Item" to start a line and pick the medicine on it, or "Add Product" to create a new one.'
              }
            >
              {editable ? (
                <AddLineButton onClick={handleAddBlankLine}>Add Item</AddLineButton>
              ) : null}
            </DetailEmpty>
          ) : (
            /* Lines already on the server and lines still being typed are the
               same lines, so they share one table and one header. */
            <Table
              className={listTableClass}
              dataSource={lines}
              columns={lineColumns}
              rowKey="key"
              /* A line with no medicine on it is the one thing on this table
                 that stops the entry, so it is marked across its width. */
              rowClassName={(row) =>
                [
                  !row.medicine && row.scannedName ? listStyles.rowFlagged : null,
                  /* A line being read about wears the organisation's colour,
                     as a selected line does on Sale Entry. */
                  row.key === historyKey ? listStyles.rowOpen : null,
                ]
                  .filter(Boolean)
                  .join(" ")
              }
              onRow={(row) => ({
                onClick: (event) => handleRowClick(event, row),
              })}
              /*
               * What the pharmacy last paid for this medicine, under the line
               * asking the question. No expand column of its own - the row is
               * what opens it, so the table keeps the shape it had.
               */
              expandable={{
                expandedRowKeys: historyKey ? [historyKey] : [],
                showExpandColumn: false,
                expandedRowRender: (row) => (
                  <InlinePurchaseHistory
                    medicineId={row.medicine?.value}
                    medicineName={row.medicine?.label}
                    currentRate={row.purchase_rate}
                    entryId={entry?.id}
                  />
                ),
                expandedRowClassName: () => listStyles.histRow,
              }}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
          )}
        </DetailCard>
        </DetailFull>

        <DetailCard title="GST Summary">
          <DetailBody>
            <GstSummaryTable items={gstLines} />
          </DetailBody>
        </DetailCard>

        <DetailCard title="Bill">
          <DetailBody>
            <BillTotals
              rows={[
                { label: "Subtotal", value: formatAmount(totals.taxable) },
                { label: "GST", value: formatAmount(totals.gst) },
                /* Cess is only a line on the bill where something actually
                   carries it; most medicines do not. */
                ...(totals.cess
                  ? [{ label: "Cess", value: formatAmount(totals.cess) }]
                  : []),
                {
                  label: "Additional Discount %",
                  control: (
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      value={additionalDiscount}
                      disabled={!editable}
                      onChange={setAdditionalDiscount}
                    />
                  ),
                },
              ]}
              totalLabel="Grand Total"
              totalValue={formatAmount(totals.grandTotal)}
            />
          </DetailBody>
        </DetailCard>
        </DetailGrid>
        </ListScreen>
      </div>

      <AuditLogDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entityType={AUDIT_ENTITIES.purchaseEntry}
        entityId={entry?.id}
        documentNumber={entry?.entry_number}
      />

      <LeaveWithoutSavingModal
        open={leaveOpen}
        saving={loading}
        detail={
          <>
            {lines.length} {lines.length === 1 ? "line" : "lines"} on this entry
            have not been saved. Saving writes the entry and leaves it as a
            draft to be finished later; stock only moves when it is completed.
          </>
        }
        onKeepEditing={() => setLeaveOpen(false)}
        onSave={saveAndLeave}
        onDiscard={() => navigate("/pharmacy/purchase-entry")}
      />

      {vendorModalOpen && (
        <VendorFormModal
          open={vendorModalOpen}
          setOpen={setVendorModalOpen}
          editData={null}
          /*
           * A vendor added from here was added in order to be used here, so it
           * goes straight into the field. Leaving it unselected meant typing
           * the name a second time to find what had just been created.
           */
          refreshPage={(created) => {
            if (!created?.id) return;
            setNewVendor({ value: created.id, label: created.name || created.vendor_name });
            form.setFieldValue("vendor", created.id);
          }}
        />
      )}

      {replaceFor && (
        <Modal
          title="Pick the medicine"
          open={Boolean(replaceFor)}
          okText="Use this medicine"
          okButtonProps={{ disabled: !replaceChoice }}
          onCancel={() => {
            setReplaceFor(null);
            setReplaceChoice(null);
          }}
          onOk={() => {
            if (!replaceChoice) return;
            updateDraft(replaceFor, { medicine: replaceChoice });
            setReplaceFor(null);
            setReplaceChoice(null);
          }}
        >
          <p style={{ marginTop: 0, color: "#667085" }}>
            The invoice calls this{" "}
            <b>{lines.find((l) => l.key === replaceFor)?.scannedName || "a product"}</b>.
            Search the catalogue to Replace.
          </p>
          <AsyncSearchSelect
            fetcher={fetchPharmacyMedicines}
            placeholder="Search the catalogue"
            value={replaceChoice?.value}
            initialOption={replaceChoice || undefined}
            onChange={(value, option) =>
              setReplaceChoice(value ? { value, label: option?.label } : null)
            }
          />
        </Modal>
      )}

      {addMedicineModalOpen && (
        <MedicineFormModal
          open={addMedicineModalOpen}
          setOpen={(open) => {
            setAddMedicineModalOpen(open);
            if (!open) setAddMedicineFor(null);
          }}
          /* A product created for a line the invoice named opens on that
             name - there is no sense in typing it a second time. */
          editData={
            addMedicineFor
              ? { name: lines.find((l) => l.key === addMedicineFor)?.scannedName || "" }
              : null
          }
          /*
           * A product created from here was created to be bought on this
           * entry: it lands on the line that asked for it, or arrives as a
           * new line when it was the card's own Add Product that asked.
           */
          refreshPage={(created) => {
            if (!created?.id) return;
            if (addMedicineFor) {
              updateDraft(addMedicineFor, {
                medicine: { value: created.id, label: created.name },
              });
              setAddMedicineFor(null);
              return;
            }
            handleSelectProduct(created.id, { label: created.name });
          }}
        />
      )}

      {draftForBatchModal && (
        <BatchDetailsModal
          open={Boolean(batchModalKey)}
          setOpen={() => setBatchModalKey(null)}
          initialValues={{
            ...draftForBatchModal.medicineDefaults,
            ...draftForBatchModal.gstDefaults,
            ...(draftForBatchModal.batch || {}),
          }}
          onSave={(values) => updateDraft(draftForBatchModal.key, { batch: values })}
        />
      )}

    </CustomLayout>
  );
};

export default PurchaseEntryPage;
